// src/services/subscriptionEnforcement.js
import { dbHelpers } from '../db/database';

const GRACE_PERIOD_DAYS = 7;
const HARD_LOCKOUT_DAYS = 30;
const MAX_OFFLINE_DAYS = 45;

class SubscriptionEnforcement {
  
  async checkAccess() {
    const token = localStorage.getItem('access_token');
    
    if (!token) {
      return {
        allowed: false,
        reason: 'not_authenticated',
        message: 'Please log in to continue',
        requiresSync: false
      };
    }

    if (navigator.onLine) {
      
      try {
        const freshData = await this.fetchSubscriptionFromBackend(token);
        
        if (freshData) {
          await this.saveSubscriptionToCache(freshData);
          return this.buildAccessStatus(freshData);
        } else {
          return {
            allowed: false,
            reason: 'no_subscription',
            message: 'No active subscription found.',
            requiresSync: false
          };
        }
      } catch (error) {
        return await this.getOfflineAccessStatus();
      }
    } else {
      return await this.getOfflineAccessStatus();
    }
  }


async fetchSubscriptionFromBackend(token) {
  try {
    const apiUrl = process.env.REACT_APP_API_URL;
    const url = `${apiUrl}/subscriptions/status/`;
        
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        const errorData = await response.json();
        
        if (errorData.error === 'NO_SUBSCRIPTION') {
        }
      }
      return null;
    }

    const data = await response.json();

    const daysRemaining = data.days_remaining !== undefined ? data.days_remaining : 0;
    const isValid = data.is_valid !== undefined ? data.is_valid : false;
    const isActive = data.is_active !== undefined ? data.is_active : false;
    
    return {
      allowed: data.allowed !== undefined ? data.allowed : (isValid && isActive && daysRemaining >= 0),
      plan: data.plan || 'unknown',
      plan_display: data.plan_display || 'Unknown',
      status: data.status || 'unknown',
      is_active: isActive,
      is_valid: isValid,
      end_date: data.end_date || null,
      days_remaining: daysRemaining,
      currency: data.currency || 'eur',
      lastSync: Date.now()
    };
  } catch (error) {
    throw error;
  }
}

  async saveSubscriptionToCache(subscriptionData) {
    try {
      if (typeof dbHelpers.saveSetting === 'function') {
        await dbHelpers.saveSetting('subscription', subscriptionData);
        await dbHelpers.saveSetting('lastSync', Date.now());
      } else {
      }
    } catch (error) {
    }
  }

  buildAccessStatus(subscription) {
    const daysRemaining = subscription.days_remaining !== undefined ? subscription.days_remaining : 0;
    const isValid = subscription.is_valid || false;
    const isActive = subscription.is_active || false;
    

    // ⭐ Valid subscription with days remaining > 0
    if (isValid && isActive && daysRemaining > 0) {
      return {
        allowed: true,
        access: 'full',
        reason: 'active',
        message: 'Subscription active',
        requiresSync: false,
        daysRemaining: daysRemaining,
        plan: subscription.plan,
        endDate: subscription.end_date,
        currency: subscription.currency
      };
    }

    // ⭐ Expires today (days_remaining = 0 but still valid)
    if (isValid && isActive && daysRemaining === 0) {
      return {
        allowed: true,
        access: 'full',
        reason: 'expires_today',
        message: 'Subscription expires today',
        warning: true,
        requiresSync: false,
        daysRemaining: 0,
        plan: subscription.plan,
        endDate: subscription.end_date,
        currency: subscription.currency
      };
    }

    // ⭐ Subscription expired or invalid
    return {
      allowed: false,
      access: 'locked',
      reason: 'expired',
      message: 'Subscription expired. Please renew.',
      requiresSync: false,
      daysRemaining: 0
    };
  }

  async getOfflineAccessStatus() {
    try {
      let subscription = null;
      let lastSync = null;

      if (typeof dbHelpers.getSetting === 'function') {
        subscription = await dbHelpers.getSetting('subscription');
        lastSync = await dbHelpers.getSetting('lastSync');
      }

      const currentTime = Date.now();

      if (!subscription || !lastSync) {
        return {
          allowed: false,
          reason: 'no_subscription',
          message: 'Please connect to internet to activate subscription.',
          requiresSync: true
        };
      }

      const daysSinceSync = Math.floor((currentTime - lastSync) / (1000 * 60 * 60 * 24));

      if (daysSinceSync > MAX_OFFLINE_DAYS) {
        return {
          allowed: false,
          reason: 'sync_required',
          message: `You've been offline for ${daysSinceSync} days. Connect to verify subscription.`,
          requiresSync: true,
          daysSinceSync: daysSinceSync
        };
      }

      if (subscription.end_date) {
        const endDate = new Date(subscription.end_date).getTime();
        const daysExpired = Math.floor((currentTime - endDate) / (1000 * 60 * 60 * 24));

        if (daysExpired > 0) {

          if (daysExpired <= GRACE_PERIOD_DAYS) {
            return {
              allowed: true,
              access: 'full',
              warning: `Subscription expired ${daysExpired} days ago. Connect to renew.`,
              requiresSync: true,
              daysExpired: daysExpired,
              daysRemaining: 0
            };
          }

          if (daysExpired <= HARD_LOCKOUT_DAYS) {
            return {
              allowed: true,
              access: 'readonly',
              message: 'Read-only mode. Connect to internet to renew subscription.',
              requiresSync: true,
              daysExpired: daysExpired,
              daysRemaining: 0
            };
          }

          return {
            allowed: false,
            reason: 'expired',
            message: `Subscription expired ${daysExpired} days ago. Connect to renew.`,
            requiresSync: true,
            daysExpired: daysExpired,
            daysRemaining: 0
          };
        }
      }

      const cachedDaysRemaining = subscription.days_remaining || 0;
      
      if (cachedDaysRemaining > 0) {
        return {
          allowed: true,
          access: 'full',
          reason: 'active',
          message: 'Subscription active (cached)',
          requiresSync: false,
          daysSinceSync: daysSinceSync,
          daysRemaining: cachedDaysRemaining
        };
      }

      return {
        allowed: false,
        reason: 'expired',
        message: 'Subscription expired. Connect to renew.',
        requiresSync: true,
        daysRemaining: 0
      };

    } catch (error) {
      return {
        allowed: false,
        reason: 'error',
        message: 'Error reading subscription status',
        requiresSync: true
      };
    }
  }

  getAllowedFeatures(accessLevel) {
    const features = {
      full: {
        viewInventory: true,
        createOrders: true,
        updateStock: true,
        viewReports: true,
        exportData: true,
        syncToServer: true
      },
      readonly: {
        viewInventory: true,
        createOrders: false,
        updateStock: false,
        viewReports: true,
        exportData: false,
        syncToServer: false
      }
    };
    return features[accessLevel] || features.readonly;
  }

  async canPerformAction(action) {
    const accessStatus = await this.checkAccess();
    
    if (!accessStatus.allowed) {
      return false;
    }
    
    const features = this.getAllowedFeatures(accessStatus.access);
    return features[action] || false;
  }

  async clearCache() {
    try {
      if (typeof dbHelpers.saveSetting === 'function') {
        await dbHelpers.saveSetting('subscription', null);
        await dbHelpers.saveSetting('lastSync', null);
      }
    } catch (error) {
      throw error;
    }
  }
}

export default new SubscriptionEnforcement();

